// Google-Aufnahme der .com (Search Console, nur lesen): „Mit Google verbinden“ oder Dienstkonto, Seiten-Status je Seite, tägliche Auffrischung im Morgen-Check
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Loader2, RefreshCw, KeyRound, Copy, Trash2, ExternalLink, CheckCircle2, CircleDashed, AlertTriangle, Upload, LogIn } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/com/google`;
const err = (e, f) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : f);
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Berlin" }) : "–");
const btn = "flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5 transition-colors disabled:opacity-50";
const USERS = "https://search.google.com/search-console/users?resource_id=sc-domain:schwalbennest-ruegen.com";
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const redirectUri = () => `${window.location.origin}/auth/google`;
const gc = (path, project) => `https://console.cloud.google.com/${path}${project ? `?project=${project}` : ""}`;

function CopyLine({ value, testid }) {
  const [done, setDone] = useState(false);
  return (
    <span className="inline-flex items-center gap-2 flex-wrap">
      <code className="bg-black/40 rounded px-2 py-1 break-all" data-testid={testid}>{value}</code>
      <button className={btn} onClick={() => { navigator.clipboard?.writeText(value); setDone(true); setTimeout(() => setDone(false), 1800); }} data-testid={`${testid}-copy`}>
        <Copy size={12} /> {done ? "Kopiert" : "Kopieren"}</button>
    </span>
  );
}

function OAuthSteps({ project }) {
  const rows = [
    ["Search Console API einschalten", gc("apis/library/searchconsole.googleapis.com", project), "„Aktivieren“ tippen."],
    ["Weiterleitungs-Adresse eintragen", gc("apis/credentials", project), <>OAuth-Client öffnen → „Autorisierte Weiterleitungs-URIs“ → URI hinzufügen → <CopyLine value={redirectUri()} testid="gsc-redirect-uri" /> → Speichern.</>],
    ["Dein Google-Konto zulassen", gc("auth/audience", project), "Unter „Testnutzer“ deine Gmail-Adresse eintragen – oder „App veröffentlichen“, dann läuft die Verbindung nicht nach 7 Tagen ab."],
    ["Mit Google verbinden", null, "Knopf unten tippen, Konto wählen und den Zugriff auf die Search Console erlauben."],
  ];
  return (
    <ol className="space-y-2 text-xs mt-3" data-testid="gsc-steps">
      {rows.map(([t, href, d], i) => (
        <li key={t} className="flex gap-3">
          <span className="w-5 h-5 rounded-full bg-white/10 grid place-items-center text-[11px] shrink-0">{i + 1}</span>
          <span><b>{href ? <a href={href} target="_blank" rel="noreferrer" className="underline underline-offset-2" data-testid={`gsc-step-link-${i + 1}`}>{t}</a> : t}</b>
            <span className="text-eb-muted"> – </span><span className="text-eb-muted">{d}</span></span>
        </li>
      ))}
    </ol>
  );
}

function KeyForm({ onSaved }) {
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const file = (e) => { const f = e.target.files?.[0]; if (f) f.text().then(setKey); };
  const save = async () => {
    setBusy(true); setMsg("");
    try { onSaved((await axios.put(`${API}/credentials`, { key })).data); setKey(""); }
    catch (e) { setMsg(err(e, "Datei nicht gespeichert")); }
    setBusy(false);
  };
  return (
    <div className="mt-3 space-y-2" data-testid="gsc-key-form">
      <p className="text-xs text-eb-muted">Zuerst die Google-Datei speichern: OAuth-Client (client_secret_….json) – oder alternativ einen Dienstkonto-Schlüssel.</p>
      <label className={`${btn} w-fit cursor-pointer`} data-testid="gsc-key-file-label">
        <Upload size={13} /> JSON-Datei auswählen
        <input type="file" accept=".json,application/json" onChange={file} className="sr-only" data-testid="gsc-key-file" />
      </label>
      <textarea value={key} onChange={(e) => setKey(e.target.value)} rows={3} placeholder='{"web": {"client_id": "…", "client_secret": "…"}}'
        className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-xs font-mono" data-testid="gsc-key-input" />
      <button className={btn} disabled={busy || key.trim().length < 20} onClick={save} data-testid="gsc-key-save">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <KeyRound size={13} />} Datei speichern</button>
      {msg && <p className="text-xs text-rose-400" data-testid="gsc-key-error">{msg}</p>}
    </div>
  );
}

function Badge({ u }) {
  if (u.indexed) return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 flex items-center gap-1 w-fit"><CheckCircle2 size={11} /> aufgenommen</span>;
  if (u.indexed === false) return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 flex items-center gap-1 w-fit"><CircleDashed size={11} /> noch nicht</span>;
  return <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-white/10 text-eb-muted flex items-center gap-1 w-fit"><AlertTriangle size={11} /> unbekannt</span>;
}

function Report({ r }) {
  const sm = r.sitemaps.find((s) => s.path?.includes("wp-sitemap")) || r.sitemaps[0];
  return (
    <div className="mt-4" data-testid="gsc-report">
      <div className="flex items-end gap-3 flex-wrap">
        <div className="text-4xl font-heading font-bold text-emerald-300" data-testid="gsc-indexed">{r.indexed}</div>
        <div className="text-sm pb-1">von <b data-testid="gsc-total">{r.total}</b> Seiten bei Google aufgenommen
          {r.unknown > 0 && <span className="text-eb-muted"> · {r.unknown} nicht prüfbar</span>}</div>
      </div>
      <p className="text-[11px] text-eb-muted mt-1" data-testid="gsc-sitemap-status">
        {sm ? `Sitemap eingereicht ${fmt(sm.last_submitted)} · ${sm.submitted} Adressen · zuletzt von Google gelesen ${fmt(sm.last_downloaded)}${sm.errors ? ` · ${sm.errors} Fehler` : ""}`
          : "Sitemap noch nicht eingereicht – in der Search Console unter „Sitemaps“ wp-sitemap.xml eintragen"} · Stand {fmt(r.at)} · {r.property}</p>
      <div className="mt-3 divide-y divide-white/5" data-testid="gsc-urls">
        {r.urls.map((u) => (
          <div key={u.url} className="py-2 grid sm:grid-cols-[150px_120px_1fr] gap-2 items-center text-xs" data-testid={`gsc-url-${u.slug}`}>
            <a href={u.url} target="_blank" rel="noreferrer" className="font-semibold truncate hover:underline">{u.title}</a>
            <Badge u={u} />
            <span className="text-eb-muted truncate">{u.error || u.coverage || "–"}{u.last_crawl ? ` · zuletzt besucht ${fmt(u.last_crawl)}` : ""}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Account({ s }) {
  if (s.mode === "oauth") return (
    <div className="flex items-center gap-2 mt-2 text-xs flex-wrap" data-testid="gsc-account">
      <span className="text-eb-muted">Verbunden mit:</span><b data-testid="gsc-account-email">{s.account_email || "Google-Konto"}</b>
      <span className="text-eb-muted">seit {fmt(s.connected_at)}</span>
    </div>
  );
  return (
    <div className="flex items-center gap-2 mt-2 text-xs flex-wrap" data-testid="gsc-account">
      <span className="text-eb-muted">Dienstkonto:</span><CopyLine value={s.client_email} testid="gsc-client-email" />
      <a className={btn} href={USERS} target="_blank" rel="noreferrer" data-testid="gsc-open-users"><ExternalLink size={12} /> Search Console: Nutzer</a>
    </div>
  );
}

export function GoogleIndex() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const back = useRef(false);
  const load = useCallback(() => axios.get(API).then((r) => setS(r.data)).catch((e) => setMsg(err(e, "Google-Status nicht ladbar"))), []);
  const act = async (k, fn) => {
    setBusy(k); setMsg("");
    try { setS((await fn()).data); } catch (e) { setMsg(err(e, "Aktion fehlgeschlagen")); }
    setBusy("");
  };
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (window.location.pathname !== "/auth/google" || back.current) { load(); return; }
    back.current = true;
    window.history.replaceState(null, "", "/?view=com");
    sessionStorage.removeItem("sn-gsc-site");
    if (q.get("error")) { setMsg(`Google-Verbindung abgebrochen (${q.get("error")})`); load(); return; }
    act("finish", () => axios.post(`${API}/oauth/finish`, { code: q.get("code"), state: q.get("state"), redirect_uri: redirectUri() })).then(load);
  }, [load]);
  const connect = async () => {
    setBusy("connect"); setMsg("");
    try { sessionStorage.setItem("sn-gsc-site", "com"); window.location.href = (await axios.post(`${API}/oauth/start`, { redirect_uri: redirectUri() })).data.url; }
    catch (e) { setMsg(err(e, "Verbinden nicht möglich")); setBusy(""); }
  };
  if (!s) return msg ? <p className="text-sm text-rose-400 mt-4" data-testid="gsc-msg">{msg}</p> : <Loader2 className="animate-spin mt-4" size={16} />;
  return (
    <div className="mt-5 pt-4 border-t border-white/5" data-testid="gsc-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm"><b>Bei Google aufgenommen</b> <span className="text-eb-muted text-xs">· aus deiner Search Console, jeden Morgen um 6 Uhr aufgefrischt</span></div>
        {s.configured && <div className="flex gap-2 flex-wrap">
          {s.connected && <button className={btn} disabled={!!busy} onClick={() => act("refresh", () => axios.post(`${API}/refresh`))} data-testid="gsc-refresh">
            {busy === "refresh" ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Jetzt bei Google abfragen</button>}
          <button className={`${btn} text-rose-300 border-rose-400/30`} disabled={!!busy} data-testid="gsc-key-delete"
            onClick={() => window.confirm("Google-Zugang aus Sunworker entfernen?") && act("del", () => axios.delete(`${API}/credentials`))}><Trash2 size={13} /> Zugang entfernen</button>
        </div>}
      </div>
      {!s.configured && <><p className="text-xs text-eb-muted mt-2">Einmalig einrichten, danach siehst du hier, welche der {s.pages} Seiten Google schon aufgenommen hat.</p><KeyForm onSaved={setS} /></>}
      {s.configured && s.mode === "oauth" && !s.connected && (
        <div data-testid="gsc-oauth-setup">
          <p className="text-xs text-eb-muted mt-2">OAuth-Client gespeichert (Projekt {s.project_id || "–"}). Noch 4 Schritte:</p>
          <OAuthSteps project={s.project_id} />
          <button className={`${btn} mt-3 bg-white text-black hover:bg-white/90 border-white`} disabled={!!busy} onClick={connect} data-testid="gsc-connect">
            {busy === "connect" || busy === "finish" ? <Loader2 size={13} className="animate-spin" /> : <LogIn size={13} />} Mit Google verbinden</button>
        </div>
      )}
      {s.connected && <Account s={s} />}
      {s.configured && s.error && <p className="text-xs text-amber-300 mt-3" data-testid="gsc-error">{s.error.msg} <span className="text-eb-muted">· {fmt(s.error.at)}</span></p>}
      {s.connected && (s.report ? <Report r={s.report} /> : <p className="text-xs text-eb-muted mt-3" data-testid="gsc-no-report">Noch kein Bericht – „Jetzt bei Google abfragen“ tippen.</p>)}
      {s.connected && s.mode === "oauth" && s.error && (
        <button className={`${btn} mt-2`} disabled={!!busy} onClick={connect} data-testid="gsc-reconnect"><LogIn size={13} /> Neu verbinden</button>
      )}
      {msg && <p className="text-xs text-rose-400 mt-2" data-testid="gsc-msg">{msg}</p>}
    </div>
  );
}
